import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { isMobile } from 'lib/device'
import cn from 'classnames'
import StreamEmbed from '../shared/StreamEmbed'
import SearchHeader from './SearchHeader'
import PanelLoadingIndicator from './PanelLoadingIndicator'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { useGetStreams } from 'refactor/hooks/api/useGetStreams'
import useStore from 'state/knovStore'
import styles from './streams.module.scss'
import panelStyles from './panel.module.scss'
import { Filter } from 'components/filters/useStreamFilters'
import { ITeam } from 'state/knovStore'
import { streamActivityTimestamp, streamsForScope } from './streamListScopes'
import { initialFeedPageFromUrl, feedPageHref } from 'lib/feedPagination'
import FeedNextPageLink from 'components/shared/FeedNextPageLink'

interface StreamsProps {
    filter?: Filter
    hideSearchHeader?: boolean
}

function Loading() {
    return <PanelLoadingIndicator label="Loading channels" />
}

const Streams: React.FC<StreamsProps> = ({ filter, hideSearchHeader = false }) => {
    const { panel, scrollContainerRef, setIsRefreshing, isRefreshing } = usePanelContext()
    const [isLocalRefreshing, setLocalIsRefreshing] = useState(false)

    // Which container's streams to list: the public directory (default), streams
    // you host ('mine'), your memberships ('following'), or private memberships
    // ('private'). Scoped lists come straight from the store — no fetch needed.
    const streamsScope = filter?.streams_scope
    const userStreams = useStore(state => state.userStreams) || []
    const currentUserId = gon.currentUser?.id
    const memberStreams = useMemo(() => {
        if (!streamsScope) return []
        return streamsForScope(userStreams, streamsScope, currentUserId)
    }, [currentUserId, userStreams, streamsScope])

    // Use the shared refresh state if available, otherwise fallback to local state
    const handleSetIsRefreshing = useCallback(
        (value: boolean) => {
            if (setIsRefreshing) {
                setIsRefreshing(value)
            } else {
                setLocalIsRefreshing(value)
            }
        },
        [setIsRefreshing],
    )

    // Use React Query hook for fetching streams
    // Linked pagination: the public /streams list is a real paginated URL
    // space for crawlers; scoped lists (mine/private) are per-viewer.
    const feedPath = streamsScope ? undefined : '/streams'
    const initialPage = initialFeedPageFromUrl(feedPath)

    const {
        streams,
        fetchNextPage,
        hasNextPage,
        nextPage,
        isFetching,
        isFetchingNextPage,
        isLoading,
        refetch,
    } = useGetStreams(!streamsScope, { initialPage })

    // Handle scroll for infinite loading
    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current
        if (!scrollContainer) return

        const onScroll = () => {
            if (scrollContainer) {
                const { scrollTop, scrollHeight, clientHeight } = scrollContainer
                if (
                    scrollTop + clientHeight >= scrollHeight - 500 &&
                    hasNextPage &&
                    !isFetchingNextPage
                ) {
                    fetchNextPage()
                }
            }
        }

        scrollContainer.addEventListener('scroll', onScroll)

        return () => {
            scrollContainer.removeEventListener('scroll', onScroll)
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, scrollContainerRef])

    const formatLastActivity = (timestamp?: string) => {
        if (!timestamp) return 'No recent activity'
        try {
            const date = new Date(timestamp)
            const now = new Date()
            const diffMs = now.getTime() - date.getTime()
            const diffMins = Math.floor(diffMs / 60000)
            const diffHours = Math.floor(diffMins / 60)
            const diffDays = Math.floor(diffHours / 24)

            if (diffDays > 0) {
                return `Active ${diffDays} day${diffDays > 1 ? 's' : ''} ago`
            } else if (diffHours > 0) {
                return `Active ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
            } else if (diffMins > 0) {
                return `Active ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
            } else {
                return 'Active just now'
            }
        } catch {
            return 'No recent activity'
        }
    }

    // Handle pull-to-refresh
    useEffect(() => {
        if (isRefreshing || (setIsRefreshing && isLocalRefreshing)) {
            if (streamsScope) {
                handleSetIsRefreshing(false)
                return
            }
            console.log('Streams - refreshing content, isRefreshing:', isRefreshing)

            // Add minimum refresh time for better UX
            const startTime = Date.now()
            refetch().then(() => {
                const elapsed = Date.now() - startTime
                const remainingTime = Math.max(0, 800 - elapsed) // Minimum 800ms refresh to match other panels

                setTimeout(() => {
                    console.log('Streams - refresh complete')
                    handleSetIsRefreshing(false)
                }, remainingTime)
            })
        }
    }, [isRefreshing, isLocalRefreshing, refetch, handleSetIsRefreshing, streamsScope])

    if (streamsScope) {
        // Membership lists (Home×Streams / Private×Streams) render from the store.
        return (
            <div className={panelStyles.questStreamComp}>
                {!hideSearchHeader && (
                    <div
                        className={cn(
                            panelStyles.searchHeaderContainer,
                            isMobile && 'full-bleed-quests',
                        )}
                    >
                        <SearchHeader filter={filter} setIsRefreshing={handleSetIsRefreshing} />
                    </div>
                )}

                {memberStreams.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>
                            {streamsScope === 'private'
                                ? 'No private channels yet'
                                : streamsScope === 'mine'
                                ? "You don't host any channels yet"
                                : "You haven't joined any channels yet"}
                        </p>
                    </div>
                ) : (
                    <div className={cn(styles.streamsContainer, isMobile && 'full-bleed-quests')}>
                        {memberStreams.map(stream => (
                            <StreamEmbed
                                key={stream.id}
                                stream={stream}
                                lastActivity={formatLastActivity(streamActivityTimestamp(stream))}
                                questCount={(stream as { quest_count?: number }).quest_count}
                            />
                        ))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={panelStyles.questStreamComp}>
            {!hideSearchHeader && (
                <div
                    className={cn(
                        panelStyles.searchHeaderContainer,
                        isMobile && 'full-bleed-quests',
                    )}
                >
                    <SearchHeader filter={filter} setIsRefreshing={handleSetIsRefreshing} />
                </div>
            )}

            {isLoading || (isFetching && !streams.length) ? (
                <Loading />
            ) : streams.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No public channels found</p>
                </div>
            ) : (
                <div className={cn(styles.streamsContainer, isMobile && 'full-bleed-quests')}>
                    {streams.map(stream => (
                        <StreamEmbed
                            key={stream.id}
                            stream={stream as ITeam}
                            lastActivity={formatLastActivity(streamActivityTimestamp(stream))}
                            questCount={stream.quest_count}
                        />
                    ))}
                    {feedPath && hasNextPage && nextPage != null && (
                        <FeedNextPageLink
                            href={feedPageHref(feedPath, nextPage)}
                            onLoadMore={fetchNextPage}
                            isLoading={isFetchingNextPage}
                        />
                    )}
                    {isFetchingNextPage && (
                        <div className={panelStyles.loader}>
                            <div className={cn(panelStyles.loadingIcon)}>
                                <i className="fa fa-circle-o-notch fa-spin fa-lg" />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default Streams
